import {
  FC,
  ComponentProps,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react';

import { i18n as I18n } from 'utils/i18n';
import { spaceDisabledForService } from 'utils/booking';
import { scrollIntoViewWithOffset } from 'utils/navigation';
import { formatDate, dateFormatForAPI } from 'utils/date';
import { AuthContext } from 'state/auth';
import { SearchContext } from 'state/search';
import useSpaceSearch, {
  getSearchData,
  SpaceSearchData,
} from 'hooks/useSpaceSearch';

import useStoredBooking, { getInitialValues } from 'hooks/useStoredBooking';
import useClientServices from 'hooks/useClientServices';
import useIsMobile from 'hooks/useIsMobile';

import FloorplansButton from '../floorplansButton';

import SpaceFilters from '../spaceFilters';
import SpaceList from '../spaceList';
import VenueCalendar from '../venueCalendar';

type View = ComponentProps<typeof SpaceFilters>['currentViewType'];

type Venue = ComponentProps<typeof SpaceList>['venue'] &
  ComponentProps<typeof VenueCalendar>['venue'];

interface Props {
  venue: Venue;
  initialData?: SpaceSearchData;
  scrollIntoView: boolean;
}

const VenueSpaces: FC<Props> = ({
  venue = {},
  initialData = getSearchData([venue]),
  scrollIntoView = false,
  ...props
}) => {
  const {
    filters: { date, ...filters } = {},
    actions: { updateDate } = {},
  } = useContext(SearchContext);

  const [{ service: serviceId } = {}] = useStoredBooking();
  const { data: services } = useClientServices();
  const service = useMemo(
    () =>
      serviceId
        ? services.find(({ id }) => id === Number(serviceId))
        : undefined,
    [services, serviceId]
  );

  const { loggedIn, accountApproved } = useContext(AuthContext);
  const isMobile = useIsMobile();

  const [view, changeViewType] = useReducer<View>(
    view => (view === 'calendar' ? 'list' : 'calendar'),
    'list'
  );

  const showCalendarToggle = !isMobile && loggedIn && accountApproved;
  const currentView = showCalendarToggle ? view : 'list';

  const dateSet = !!date;

  useEffect(() => {
    if (currentView === 'calendar' && !dateSet) updateDate(new Date());
  }, [dateSet, updateDate, currentView]);

  useEffect(() => {
    if (scrollIntoView) {
      window.scrollTo(0, 1800);
    }
  }, [scrollIntoView]);

  const { distance, capacity, facets } = filters;

  const {
    data: { venues: [{ spaces = [] } = {}] = [], aggregates } = initialData,
  } = useSpaceSearch({
    viewType: currentView,
    filters: {
      distance,
      capacity,
      facets,
      ...(currentView === 'calendar' ? {} : { date }),
      selectedVenueIds: venue.id ? [venue.id] : [],
    },
  });

  useEffect(() => {
    if (scrollIntoView && spaces.length > 0) {
      setTimeout(() => {
        scrollIntoViewWithOffset('#venue-spaces', 100);
      }, 200);
    }
  }, [scrollIntoView, spaces]);

  const renderList = () => {
    return spaces.length > 0 ? (
      <SpaceList
        {...props}
        spaces={spaces}
        spaceServiceClash={spaceDisabledForService(service)}
        venue={venue}
        onAddToBooking={(venue, space) => {
          props.onAddToBooking(venue, space, { date });
        }}
        fetchSpaces={false}
      />
    ) : (
      <div className="venue-search__alternative_venues_message">
        <p>
          {I18n.t(
            'search_results.alternative_venues.no_matches_with_capacity_filter'
          )}
        </p>
      </div>
    );
  };

  const renderCalendar = () => (
    <VenueCalendar
      {...props}
      spaces={spaces}
      venue={venue}
      spaceServiceClash={spaceDisabledForService(service)}
      date={date || formatDate(new Date(), dateFormatForAPI)}
      setDate={updateDate}
      initialValues={{ ...getInitialValues(), date }}
    />
  );

  return (
    <div id="venue-spaces" className="venue-spaces" data-cy="venueSpaces">
      <h2>{`Rooms at ${venue.name}`}</h2>
      <SpaceFilters
        showPropertyFilter={false}
        currentViewType={currentView}
        changeViewType={showCalendarToggle ? changeViewType : undefined}
        aggregates={aggregates}
      >
        <FloorplansButton venue={venue} />
      </SpaceFilters>
      {currentView === 'calendar' ? renderCalendar() : renderList()}
    </div>
  );
};

export default VenueSpaces;
